import React, { useLayoutEffect, useRef, useState } from 'react';
import './spherecarousel.css';
import ExperienceCard from './experiencecard';
import { gsap, prefersReducedMotion, ScrollTrigger } from '../hooks/useGsapAnimations';
 
const SphereCarousel = ({ cards }) => {
  const [activeIndex, setActiveIndex] = useState(Math.floor(cards.length / 2));
  const stageRef = useRef(null);
 
  const R = 280;
  const ANGLE_STEP = 32;

  useLayoutEffect(() => {
    const stage = stageRef.current;

    if (!stage || prefersReducedMotion()) return undefined;

    const ctx = gsap.context(() => {
      const scroller = stage.closest('.page_content') || undefined;

      gsap.from('.experience_card', {
        autoAlpha: 0,
        y: 38,
        scale: 0.84,
        rotateY: -24,
        duration: 0.9,
        ease: 'back.out(1.45)',
        stagger: {
          each: 0.08,
          from: 'center',
        },
        clearProps: 'transform,opacity,visibility',
        scrollTrigger: {
          trigger: stage,
          scroller,
          start: 'top 84%',
          toggleActions: 'play none none reverse',
        },
      });
    }, stage);

    const refreshId = window.setTimeout(() => ScrollTrigger.refresh(), 0);

    return () => {
      window.clearTimeout(refreshId);
      ctx.revert();
    };
  }, [cards.length]);

  useLayoutEffect(() => {
    const stage = stageRef.current;

    if (!stage || prefersReducedMotion()) return undefined;

    const activeCard = stage.querySelector('.active_card');
    if (!activeCard) return undefined;

    const ctx = gsap.context(() => {
      gsap.from(activeCard.querySelector('.experience_object'), {
        autoAlpha: 0,
        y: 24,
        rotate: -5,
        scale: 0.9,
        duration: 0.65,
        ease: 'back.out(1.7)',
        clearProps: 'transform,opacity,visibility',
      });

      gsap.from(activeCard.querySelectorAll('.experience_content > *, .live_badge'), {
        autoAlpha: 0,
        y: 14,
        duration: 0.45,
        ease: 'power3.out',
        stagger: 0.05,
        clearProps: 'transform,opacity,visibility',
      });
    }, stage);

    return () => ctx.revert();
  }, [activeIndex]);
 
  const getStyle = (index) => {
    const offset = index - activeIndex;
    const angle = (offset * ANGLE_STEP * Math.PI) / 180;
    const x = R * Math.sin(angle);
    const rotY = -offset * ANGLE_STEP;
    const scale = offset === 0 ? 1 : 0.62 + 0.38 * Math.cos(angle);
    const opacity = offset === 0 ? 1 : 0.4 + 0.6 * Math.cos(angle);
    const zIndex = offset === 0 ? 10 : 10 - Math.abs(offset);
 
    return {
      transform: `translateX(${x}px) rotateY(${rotY}deg) scale(${scale})`,
      opacity,
      zIndex,
    };
  };
 
  let touchStartX = 0;
  const onTouchStart = (e) => { touchStartX = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) > 40) {
      const next = activeIndex + (dx < 0 ? 1 : -1);
      if (next >= 0 && next < cards.length) setActiveIndex(next);
    }
  };

  const onKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const next = activeIndex + (event.key === 'ArrowRight' ? 1 : -1);

    if (next >= 0 && next < cards.length) {
      setActiveIndex(next);
    }
  };
 
  return (
    <div
      className="carousel_stage"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onKeyDown={onKeyDown}
      ref={stageRef}
      role="region"
      aria-label="Techne experiences carousel. Use left and right arrow keys to change experience."
      tabIndex={0}
    >
      {cards.map((card, index) => (
        <div key={card.number} className="carousel_slot" style={getStyle(index)} aria-hidden={index !== activeIndex}>
          <ExperienceCard
            number={card.number}
            title={card.title}
            description={card.description}
            image={card.image}
            status={card.status}
            background={card.background}
            light={card.light}
            active={index === activeIndex}
          />
        </div>
      ))}
    </div>
  );
};
 
export default SphereCarousel;
